import { Assert } from "./Assert";
import { PartialNullable } from "./Extensions";
import { PathHelper } from "./Helpers/PathHelper";
import { StringHelper } from "./Helpers/StringHelper";
import { NAMESPACE } from "./_Namespace";
import { PropertyChange } from "./PropertyChange";
import { IRadObject, Property, PropertyBoolean, PropertyObject, PropertyString, RadObject, RadObjectPropertyValues } from "./RadObject";
import { ApplicationInformationService } from "./RadWindow";
import { ServiceManager } from "./ServiceManager";

export type EntityCommandInformationPropertyValues =
    RadObjectPropertyValues
    & {
        caller: any;
        entityName: string;
        entityNameFriendly: string;
        entitySetName: string;
        entitySetNameFriendly: string;
        entitySupportsSoftDelete: boolean;
        foreignEntityAlias: string;
        openRecordInDialog: boolean;
        parentEntityId: string;
        parentEntityName: string;
        parentEntitySetName: string;
        parentEntityUrl: string;
        record: any;
        records: any[];
    };

export type EntityCommandInformationProperties = {
    [name in keyof EntityCommandInformationPropertyValues]: Property<EntityCommandInformationPropertyValues[name]>
};

export interface IEntityCommandInformation extends IRadObject, EntityCommandInformationProperties
{
}

export class EntityCommandInformation extends RadObject<EntityCommandInformationPropertyValues> implements IEntityCommandInformation
{
    public static override classNamespace = NAMESPACE + "EntityCommandInformation";
    public static override className = "EntityCommandInformation";

    // #region Properties

    public caller!: PropertyObject;
    public entityName!: PropertyString;
    public entityNameFriendly!: PropertyString;
    public entitySetName!: PropertyString;
    public entitySetNameFriendly!: PropertyString;
    public entitySupportsSoftDelete!: PropertyBoolean;
    public foreignEntityAlias!: PropertyString;
    public openRecordInDialog!: PropertyBoolean;
    public parentEntityId!: PropertyString;
    public parentEntityName!: PropertyString;
    public parentEntitySetName!: PropertyString;
    public parentEntityUrl!: PropertyString;
    public record!: PropertyObject;
    public records!: Property<any[]>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<EntityCommandInformationPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Public Methods

    public createEntityUrl()
    {
        const
            entitySetName = this.entitySetName.get(),
            foreignEntityAlias = this.foreignEntityAlias.get(),
            parentEntityId = this.parentEntityId.get(),
            parentEntitySetName = this.parentEntitySetName.get(),
            parentEntityUrl = this.parentEntityUrl.get(),
            record = this.record.get(),
            recordId = record?.id || "new";

        if (!foreignEntityAlias)
            return StringHelper.format("{0}{1}/{2}/", ServiceManager.getRequired(ApplicationInformationService).virtualDirectory, entitySetName?.toLowerCase(), recordId);
        else
        {
            Assert.isNotNullOrUndefined(parentEntityUrl, "parentEntityUrl");

            const
                location = PathHelper.getLocation(parentEntityUrl);

            Assert.isNotNullOrUndefined(location.applicationRelativeUrl, "location.applicationRelativeUrl");

            return (
                StringHelper.trimEnd(location.applicationRelativeUrl, "/").split("/").length > 2
                    ? StringHelper.format("{0}{1}/{2}/{3}/{4}/", ServiceManager.getRequired(ApplicationInformationService).virtualDirectory, parentEntitySetName?.toLowerCase(), parentEntityId, foreignEntityAlias.toLowerCase(), recordId)
                    : StringHelper.format("{0}{1}/{2}/", location.pathname, foreignEntityAlias.toLowerCase(), recordId)
            );
        }
    }
    public getRecordId()
    {
        const
            record = this.record.get();

        return record ? record.id : null;
    }

    // #endregion

    // #region Protected Methods

    protected _callerChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _entityNameChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _entityNameFriendlyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _entitySetNameChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _entitySetNameFriendlyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _entitySupportsSoftDeleteChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _foreignEntityAliasChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _openRecordInDialogChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _parentEntityIdChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _parentEntityNameChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _parentEntitySetNameChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _parentEntityUrlChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _recordChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _recordsChanged(change: PropertyChange<any[]>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: EntityCommandInformationPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.caller = new PropertyObject(this, propertyValues.caller, this._callerChanged);
        this.entityName = new PropertyString(this, propertyValues.entityName, this._entityNameChanged);
        this.entityNameFriendly = new PropertyString(this, propertyValues.entityNameFriendly, this._entityNameFriendlyChanged);
        this.entitySetName = new PropertyString(this, propertyValues.entitySetName, this._entitySetNameChanged);
        this.entitySetNameFriendly = new PropertyString(this, propertyValues.entitySetNameFriendly, this._entitySetNameFriendlyChanged);
        this.entitySupportsSoftDelete = new PropertyBoolean(this, propertyValues.entitySupportsSoftDelete, this._entitySupportsSoftDeleteChanged);
        this.foreignEntityAlias = new PropertyString(this, propertyValues.foreignEntityAlias, this._foreignEntityAliasChanged);
        this.openRecordInDialog = new PropertyBoolean(this, propertyValues.openRecordInDialog, this._openRecordInDialogChanged);
        this.parentEntityId = new PropertyString(this, propertyValues.parentEntityId, this._parentEntityIdChanged);
        this.parentEntityName = new PropertyString(this, propertyValues.parentEntityName, this._parentEntityNameChanged);
        this.parentEntitySetName = new PropertyString(this, propertyValues.parentEntitySetName, this._parentEntitySetNameChanged);
        this.parentEntityUrl = new PropertyString(this, propertyValues.parentEntityUrl, this._parentEntityUrlChanged);
        this.record = new PropertyObject(this, propertyValues.record, this._recordChanged);
        this.records = new Property<any[]>(this, "System.Object, System.Private.CoreLib", propertyValues.records, this._recordsChanged);
    }

    // #endregion
}

